import { v4 as uuidv4 } from 'uuid';

import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import {
  addConditionGroupAction,
  addConditionOptionAction,
  addConditionToConditionAction,
  addMediaElementAction,
  createEmptyConditionAction,
  setSlotLayoutAction,
} from 'editor/src/store/design/slice';
import { ConditionOption, SlotLayout, SlotLayoutDraft } from 'editor/src/store/design/types';
import buildSlotPictureElement from 'editor/src/store/design/util/slotLayout/buildSlotPictureElement';
import computeSlotFrames, { getMismatchedCounts } from 'editor/src/store/design/util/slotLayout/computeSlotFrames';
import getSlotFileSetOptionIndexes from 'editor/src/store/design/util/slotLayout/getSlotFileSetOptionIndexes';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import { conditionOptionUuids, conditionUuids, elementUuids } from 'editor/src/util/uuids';

import attachSlotElementToProduct from './attachSlotElementToProduct';
import updateHiddenElementsOperation from './updateHiddenElementsOperation';

export const MIN_PRODUCTS = 2;

/** Cleans the author's option list: a choice needs at least two options to be one. */
export function getProductOptions(draft: Pick<SlotLayoutDraft, 'productOptions'>): string[] {
  const options = draft.productOptions.map((label) => label.trim()).filter(Boolean);
  return options.length >= 2 ? options : [];
}

export function isSlotLayoutDraftComplete(draft: SlotLayoutDraft): boolean {
  return (
    draft.name.trim().length > 0 &&
    (getProductOptions(draft).length === 0 || draft.productLabel.trim().length > 0) &&
    draft.maxProducts >= MIN_PRODUCTS &&
    draft.rows.length === draft.maxProducts &&
    getMismatchedCounts(draft.rows).length === 0 &&
    draft.gap >= 0 &&
    // A gap wider than the area would leave frames of negative size.
    computeSlotFrames(draft.rows, draft.area, draft.gap).every((frame) => frame.width > 0 && frame.height > 0)
  );
}

const newOption = (label: string): ConditionOption => ({
  id: conditionOptionUuids.generate(),
  label,
  imageId: undefined,
});

/**
 * Builds a product layout from the author's draft: the count picklist (buttons 1…N), one
 * picklist per product nested under the counts that show it, and a picture layer per frame
 * and picture set, linked per product. Returns the layout id, or nothing when the draft is
 * not complete.
 */
const createSlotLayoutOperation =
  (spreadIndex: number, draft: SlotLayoutDraft) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    const spread = state.design.designData?.spreads[spreadIndex];
    if (!spread || !isSlotLayoutDraftComplete(draft)) {
      return undefined;
    }

    const layoutId = `slot-${uuidv4()}`;
    const productOptions = getProductOptions(draft);
    const frames = computeSlotFrames(draft.rows, draft.area, draft.gap);

    batch(() => {
      dispatch(saveUndoRedoStateOperation('Product layout'));
      if (!spread.conditionGroup) {
        dispatch(addConditionGroupAction({ spreadIndex }));
      }

      const conditionId = conditionUuids.generate();
      const countOptions = Array.from({ length: draft.maxProducts }, (_, i) => newOption(String(i + 1)));
      dispatch(
        createEmptyConditionAction({
          address: { spreadIndex, conditionId },
          name: draft.name.trim(),
          option1: countOptions[0],
          option2: countOptions[1],
        }),
      );
      countOptions.slice(2).forEach((option) => {
        dispatch(
          addConditionOptionAction({ address: { spreadIndex, conditionId }, optionId: option.id, label: option.label }),
        );
      });

      // Product k is asked about from count k on: its picklist hangs under every count option
      // that places it, so the buyer sees exactly one picklist per product on the page.
      const productConditionIds: string[] = [];
      if (productOptions.length) {
        for (let product = 1; product <= draft.maxProducts; product += 1) {
          const productConditionId = conditionUuids.generate();
          const options = productOptions.map(newOption);
          dispatch(
            createEmptyConditionAction({
              address: { spreadIndex, conditionId: productConditionId },
              name: `${draft.productLabel.trim()} ${product}`,
              option1: options[0],
              option2: options[1],
              parent: { conditionId, optionId: countOptions[product - 1].id },
            }),
          );
          options.slice(2).forEach((option) => {
            dispatch(
              addConditionOptionAction({
                address: { spreadIndex, conditionId: productConditionId },
                optionId: option.id,
                label: option.label,
              }),
            );
          });
          countOptions.slice(product).forEach((countOption) => {
            dispatch(
              addConditionToConditionAction({
                spreadIndex,
                conditionId: productConditionId,
                parent: { conditionId, optionId: countOption.id },
              }),
            );
          });
          productConditionIds.push(productConditionId);
        }
      }

      const layout: SlotLayout = {
        ...draft,
        name: draft.name.trim(),
        productLabel: draft.productLabel.trim(),
        productOptions,
        id: layoutId,
        conditionId,
        productConditionIds,
        frames,
      };

      frames.forEach((frame) => {
        draft.fileSets.forEach((fileSet) => {
          const folderRef = fileSet.folders[frame.columns];
          const element = buildSlotPictureElement({
            layoutId,
            frame,
            fileSet,
            folder: folderRef ? state.collections.folders[folderRef.folderId] : undefined,
            uuid: elementUuids.generate(),
            name: `${fileSet.name} ${frame.product}`,
          });
          if (!element) {
            return;
          }
          dispatch(addMediaElementAction({ spreadIndex, pageIndex: 0, element }));
          attachSlotElementToProduct(
            dispatch,
            getState,
            spreadIndex,
            layout,
            frame.product,
            element,
            getSlotFileSetOptionIndexes(fileSet, productOptions),
          );
        });
      });

      dispatch(setSlotLayoutAction({ spreadIndex, layout }));
      dispatch(updateHiddenElementsOperation(spreadIndex));
    });

    return layoutId;
  };

export default createSlotLayoutOperation;
